import React, { useEffect, useState } from "react";
import { useLiveRefresh } from "@/hooks/useLiveRefresh";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatCr, formatINRFull, formatNumber, formatPct } from "@/lib/format";
import {
  Wallet, Banknote, Percent, AlertTriangle, ShieldAlert, Users, Receipt, HandCoins,
  Bell, Printer, TrendingUp, Layers,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend, LabelList,
  LineChart, Line, ComposedChart, Area, ResponsiveContainer,
} from "recharts";
import { Kpi, ChartCard, Chart, PALETTE, tooltipStyle } from "@/components/charts";

const BUCKET_COLORS = {
  "On time": "#0ca678", "0-30 DPD": "#82c91e", "31-60 DPD": "#e8a200",
  "61-90 DPD": "#fd7e14", "91-365 DPD": "#b3122a", "Closed": "#94a3b8",
};

export default function Dashboard() {
  const [version] = useLiveRefresh();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [c, setC] = useState(null);
  const [pipe, setPipe] = useState(null);
  const [trend, setTrend] = useState(null);
  const [vintage, setVintage] = useState(null);
  const [fac, setFac] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [dates, setDates] = useState([]);
  const [asOf, setAsOf] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/portfolio/as-of-dates").then((r) => { setDates(r.data.dates); setAsOf(current => current || r.data.latest || ""); }).catch(() => {});
    api.get("/portfolio/trends").then((r) => setTrend(r.data.trend)).catch(() => {});
    api.get("/portfolio/vintage").then((r) => setVintage(r.data.vintage)).catch(() => {});
    api.get("/portfolio/alerts").then((r) => setAlerts(r.data.alerts || [])).catch(() => {});
    api.get("/pipeline/summary").then((r) => setPipe(r.data)).catch(() => {});
  }, [version]);

  useEffect(() => {
    const params = asOf ? { as_of: asOf } : {};
    Promise.all([api.get("/portfolio/summary", { params }), api.get("/portfolio/charts", { params })])
      .then(([a, b]) => { setS(a.data); setC(b.data); })
      .catch((e) => setError(e.response?.data?.detail || e.message));
    api.get("/portfolio/facilities", { params }).then((r) => setFac(r.data)).catch(() => {});
  }, [asOf, version]);

  const goLoans = (q = {}) => navigate({ pathname: "/portfolio", search: new URLSearchParams(q).toString() });

  if (error) return <div className="p-8 text-[#b3122a]" data-testid="dashboard-error">Failed to load: {error}</div>;
  if (!s || !c) return <div className="p-8 text-slate-400" data-testid="dashboard-loading">Loading portfolio…</div>;

  const facs = (fac && fac.facilities) || [];
  const TOP = 15;
  const heroData = facs.slice(0, TOP).map((f, i) => ({
    name: f.borrower, value: f.outstanding, roi: f.roi, id: f.id,
    pct: fac && fac.aum ? (f.outstanding / fac.aum * 100) : 0, color: PALETTE[i % PALETTE.length],
  }));
  const restVal = facs.slice(TOP).reduce((a, b) => a + b.outstanding, 0);
  if (restVal > 0) heroData.push({
    name: `Others (${facs.length - TOP})`, value: restVal, roi: null, id: null,
    pct: fac && fac.aum ? (restVal / fac.aum * 100) : 0, color: "#cbd5e1",
  });

  return (
    <div className="p-6 lg:p-8" data-testid="dashboard-page">
      <header className="mb-6 flex items-start justify-between flex-wrap gap-3 print:block">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-[#1a1a1b]">Red Fort Capital Loan Pulse · Overview</h1>
          <p className="text-slate-500 mt-1">{formatNumber(s.total_facilities)} unique facilities · {formatNumber(s.active_facilities)} active</p>
        </div>
        <div className="flex items-center gap-3 print:hidden">
          <label className="text-xs font-semibold text-slate-500">As of</label>
          <select value={asOf} onChange={(e) => setAsOf(e.target.value)} data-testid="as-of-selector"
            className="px-3 py-2 rounded-sm border border-slate-300 bg-white text-sm font-medium text-slate-700 focus:border-[#b3122a] outline-none cursor-pointer">
            {dates.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <button onClick={() => window.print()} data-testid="board-pack-btn"
            className="flex items-center gap-2 px-3 py-2 rounded-sm bg-[#111111] text-white text-sm font-semibold hover:bg-[#1a1a1b] transition">
            <Printer size={16} /> Board Pack
          </button>
        </div>
      </header>

      {/* Hero — AUM composition by facility */}
      {fac && heroData.length > 0 && (
        <div className="rf-card p-6 mb-6 border-t-4 border-t-[#b3122a]" data-testid="hero-aum">
          <div className="flex items-baseline justify-between mb-1 flex-wrap gap-2">
            <h2 className="font-display text-xl font-bold text-[#1a1a1b]">Portfolio Composition · AUM {formatCr(fac.aum)}</h2>
            <span className="text-sm text-slate-500">{fac.count} active facilities · % of AUM with interest rate</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center mt-3">
            <div className="min-w-0">
              <ResponsiveContainer width="99%" height={360}>
                <PieChart>
                  <Pie data={heroData} dataKey="value" nameKey="name" innerRadius={78} outerRadius={140} paddingAngle={1}
                    isAnimationActive={false}
                    onClick={(e) => e && e.id && navigate(`/portfolio/${e.id}`)} cursor="pointer">
                    {heroData.map((d) => <Cell key={d.name} fill={d.color} />)}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle}
                    formatter={(v, n, p) => [`${formatINRFull(v)} · ${p.payload.pct.toFixed(1)}% of AUM${p.payload.roi != null ? ` · ${p.payload.roi}% ROI` : ""}`, p.payload.name]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="min-w-0 max-h-[340px] overflow-y-auto pr-1" data-testid="hero-legend">
              {heroData.map((d) => (
                <button key={d.name} onClick={() => d.id && navigate(`/portfolio/${d.id}`)} disabled={!d.id}
                  className={`w-full flex items-center gap-3 py-2 px-2 rounded-sm text-left transition ${d.id ? "hover:bg-slate-50 cursor-pointer" : "cursor-default"}`}
                  data-testid={`hero-legend-row-${d.id || "others"}`}>
                  <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: d.color }} />
                  <span className="flex-1 text-sm font-semibold text-[#1a1a1b] truncate">{d.name}</span>
                  {d.roi != null && <span className="text-xs font-bold text-[#0e7490] tabular-nums">{d.roi}%</span>}
                  <span className="text-sm font-bold text-[#b3122a] tabular-nums w-14 text-right">{d.pct.toFixed(1)}%</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Alerts banner */}
      {alerts.length > 0 && (
        <div className="rf-card p-4 mb-6 border-l-4 border-l-[#b3122a] bg-[#b3122a]/[0.04]" data-testid="alerts-banner">
          <div className="flex items-start gap-3">
            <Bell size={18} className="text-[#b3122a] mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-[#1a1a1b] text-sm">{alerts.length} active alert{alerts.length > 1 ? "s" : ""}</div>
              <ul className="mt-1 space-y-0.5">
                {alerts.map((a, i) => (
                  <li key={`${a.type}-${i}`} className="text-sm text-slate-600"><span className="font-semibold text-[#b3122a]">{a.type}:</span> {a.message}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Primary KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
        <Kpi icon={Wallet} label="AUM (Outstanding)" value={formatCr(s.aum)} sub={`${formatNumber(s.active_facilities)} active facilities`} tone="red" delay={0} testid="kpi-aum" highlight onClick={() => goLoans()} />
        <Kpi icon={Banknote} label="Total Disbursed" value={formatCr(s.disbursed)} sub={`Avg ticket ${formatCr(s.avg_ticket)}`} tone="blue" delay={60} testid="kpi-disbursed" onClick={() => goLoans()} />
        <Kpi icon={Percent} label="Weighted Yield" value={formatPct(s.weighted_yield)} sub="ROI weighted by AUM" tone="green" delay={120} testid="kpi-yield" />
        <Kpi icon={Receipt} label="Monthly EMI Demand" value={formatCr(s.monthly_emi)} sub="Scheduled inflow" tone="purple" delay={180} testid="kpi-emi" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        <Kpi icon={AlertTriangle} label="PAR (0-90 DPD)" value={formatCr(s.par_amount)} sub={`${formatPct(s.par_pct)} of AUM · ${formatNumber(s.overdue_accounts)} accts`} tone="amber" delay={0} testid="kpi-par" onClick={() => goLoans({ bucket: "0-30 DPD" })} />
        <Kpi icon={ShieldAlert} label="GNPA (90+ DPD)" value={formatCr(s.gnpa_amount)} sub={`${formatPct(s.gnpa_pct)} of AUM · ${formatNumber(s.gnpa_accounts)} accts`} tone="red" delay={60} testid="kpi-gnpa" onClick={() => goLoans({ bucket: "91-365 DPD" })} />
        <Kpi icon={HandCoins} label="Collections (mo)" value={formatCr(s.collections_current)} sub={`Overdue recovered ${formatCr(s.collections_overdue)}`} tone="green" delay={120} testid="kpi-collections" />
        <Kpi icon={Users} label="Pipeline (LOS)" value={pipe ? formatNumber(pipe.total_applications) : "—"} sub={pipe ? `${formatNumber(pipe.approved)} approved · ${formatPct(pipe.approval_rate)}` : ""} tone="blue" delay={180} testid="kpi-pipeline" onClick={() => navigate("/pipeline")} />
      </div>

      {/* Trends */}
      {trend && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
          <ChartCard title="AUM & Disbursements" subtitle="12-month trend (₹)" testid="chart-aum-trend">
            <Chart height={280}>
              <ComposedChart data={trend} margin={{ left: -5, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef0f5" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => formatCr(v)} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={60} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatINRFull(v)} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="disbursements" fill="#1f6feb" radius={[4, 4, 0, 0]} name="Disbursements" barSize={16} />
                <Line type="monotone" dataKey="aum" stroke="#b3122a" strokeWidth={2.5} dot={false} name="AUM" />
              </ComposedChart>
            </Chart>
          </ChartCard>

          <ChartCard title="PAR % & GNPA %" subtitle="12-month risk trend" testid="chart-risk-trend">
            <Chart height={280}>
              <LineChart data={trend} margin={{ left: -10, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef0f5" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={40} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => `${v}%`} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="par_pct" stroke="#e8a200" strokeWidth={2.5} dot={false} name="PAR %" />
                <Line type="monotone" dataKey="gnpa_pct" stroke="#b3122a" strokeWidth={2.5} dot={false} name="GNPA %" />
              </LineChart>
            </Chart>
          </ChartCard>

          <ChartCard title="Collection Efficiency" subtitle="Collected vs scheduled demand (%)" testid="chart-collection-eff">
            <Chart height={260}>
              <ComposedChart data={trend} margin={{ left: -5, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef0f5" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="l" tickFormatter={(v) => formatCr(v)} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={55} />
                <YAxis yAxisId="r" orientation="right" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={42} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="l" dataKey="demand" fill="#cbd5e1" radius={[4, 4, 0, 0]} name="Demand" barSize={14} />
                <Bar yAxisId="l" dataKey="collections" fill="#0ca678" radius={[4, 4, 0, 0]} name="Collected" barSize={14} />
                <Line yAxisId="r" type="monotone" dataKey="collection_efficiency" stroke="#b3122a" strokeWidth={2.5} dot={false} name="Efficiency %" />
              </ComposedChart>
            </Chart>
          </ChartCard>

          <ChartCard title="PAR / GNPA by Vintage" subtitle="By disbursement cohort year" testid="chart-vintage">
            <Chart height={260}>
              <BarChart data={vintage || []} margin={{ left: -10, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef0f5" vertical={false} />
                <XAxis dataKey="cohort" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={40} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => `${v}%`} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="par_pct" fill="#e8a200" radius={[4, 4, 0, 0]} name="PAR %" barSize={16} />
                <Bar dataKey="gnpa_pct" fill="#b3122a" radius={[4, 4, 0, 0]} name="GNPA %" barSize={16} />
              </BarChart>
            </Chart>
          </ChartCard>
        </div>
      )}

      {/* Concentration + composition */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        <ChartCard title="AUM by Product" subtitle="Outstanding (₹) · click to filter" className="lg:col-span-2" testid="chart-product">
          <Chart height={300}>
            <BarChart data={c.by_product} margin={{ left: -5, right: 10 }} onClick={(e) => e?.activeLabel && goLoans({ product: e.activeLabel })}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef0f5" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval={0} angle={-12} textAnchor="end" height={70} />
              <YAxis tickFormatter={(v) => formatCr(v)} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={70} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatINRFull(v)} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} name="AUM" barSize={38} cursor="pointer">
                {c.by_product.map((d, i) => <Cell key={d.name} fill={PALETTE[i % PALETTE.length]} />)}
              </Bar>
            </BarChart>
          </Chart>
        </ChartCard>

        <ChartCard title="DPD Aging" subtitle="Facilities by bucket · click to filter" testid="chart-dpd">
          <Chart height={300}>
            <PieChart>
              <Pie data={c.dpd_buckets} dataKey="count" nameKey="name" innerRadius={55} outerRadius={92} paddingAngle={2}
                onClick={(e) => e?.name && goLoans({ bucket: e.name })} cursor="pointer">
                {c.dpd_buckets.map((d, i) => <Cell key={d.name} fill={BUCKET_COLORS[d.name] || PALETTE[i]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} formatter={(v, n, p) => [`${formatNumber(v)} accts`, p.payload.name]} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </Chart>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <ChartCard title="All Borrowers" subtitle={`${facs.length} active facilities · principal remaining vs original · click to open`} testid="chart-borrowers">
          <div className="max-h-[320px] overflow-y-auto -mx-1" data-testid="all-borrowers-list">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="text-left text-[10px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                  <th className="py-2 px-2 w-8">#</th>
                  <th className="py-2 px-2">Borrower</th>
                  <th className="py-2 px-2 text-right">Principal Remaining</th>
                  <th className="py-2 px-2 text-right">Original</th>
                  <th className="py-2 px-2 text-right">ROI</th>
                </tr>
              </thead>
              <tbody>
                {facs.map((f, i) => (
                  <tr key={f.id} onClick={() => navigate(`/portfolio/${f.id}`)}
                    className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition" data-testid={`borrower-row-${f.id}`}>
                    <td className="py-2 px-2 text-slate-400 tabular-nums">{i + 1}</td>
                    <td className="py-2 px-2 font-semibold text-[#1a1a1b] truncate max-w-[200px]">{f.borrower}</td>
                    <td className="py-2 px-2 text-right font-bold text-[#b3122a] tabular-nums" data-testid={`overview-principal-${f.id || f.facility_number}`}>{formatINRFull(f.outstanding_principal)}</td>
                    <td className="py-2 px-2 text-right text-slate-500 tabular-nums" data-testid={`overview-sanction-${f.id || f.facility_number}`}>{formatINRFull(f.sanction_amount)}</td>
                    <td className="py-2 px-2 text-right font-bold text-[#0e7490] tabular-nums">{f.roi}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartCard>

        <ChartCard title="Yield vs Risk by Product" subtitle="Weighted yield vs PAR % (bubble = AUM)" testid="chart-yvr">
          <Chart height={320}>
            <BarChart data={c.yield_vs_risk} margin={{ left: -10, right: 10 }} onClick={(e) => e?.activeLabel && goLoans({ product: e.activeLabel })}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef0f5" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval={0} angle={-12} textAnchor="end" height={70} />
              <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={40} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => `${v}%`} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="yield" fill="#0ca678" radius={[4, 4, 0, 0]} name="Yield %" barSize={18} />
              <Bar dataKey="par_pct" fill="#b3122a" radius={[4, 4, 0, 0]} name="PAR %" barSize={18} />
            </BarChart>
          </Chart>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ChartCard title="AUM by Geography" subtitle="Top states · click to filter" testid="chart-geography">
          <Chart height={300}>
            <BarChart data={c.by_geography} layout="vertical" margin={{ left: 30, right: 30 }} onClick={(e) => e?.activeLabel && goLoans({ geography: e.activeLabel })}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef0f5" horizontal={false} />
              <XAxis type="number" tickFormatter={(v) => formatCr(v)} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} width={110} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatINRFull(v)} />
              <Bar dataKey="value" fill="#1f6feb" radius={[0, 6, 6, 0]} name="AUM" barSize={15} cursor="pointer" />
            </BarChart>
          </Chart>
        </ChartCard>

        <ChartCard title="AUM by Industry" subtitle="Top industries" testid="chart-industry">
          <Chart height={300}>
            <BarChart data={c.by_industry} layout="vertical" margin={{ left: 30, right: 30 }} onClick={(e) => e?.activeLabel && goLoans({ industry: e.activeLabel })}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef0f5" horizontal={false} />
              <XAxis type="number" tickFormatter={(v) => formatCr(v)} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} width={140} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatINRFull(v)} />
              <Bar dataKey="value" fill="#0ca678" radius={[0, 6, 6, 0]} name="AUM" barSize={15} cursor="pointer" />
            </BarChart>
          </Chart>
        </ChartCard>
      </div>
    </div>
  );
}
